import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Button, IconButton, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Highlight, PdfHighlighter, PdfLoader } from 'react-pdf-highlighter';
import 'react-pdf-highlighter/dist/style.css';
import TranslateSelection from './text-selection';
import { getDocumentByPathAsync } from '../../../../application/features/proposalDocument/proposalDocumentThunk';
import { useAppDispatch } from '../../../../application/store/useStore';
import { ArrowOutward } from '@mui/icons-material';
import { Icon } from '@iconify/react';
import { SearchZoomIn } from 'iconsax-react';
import './styles.scss';
const ChatReference = ({ sources, currentSource, open, hash_id, scroll_id, onClose, onZoomToggle, setZoomView, onReferenceClick }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const isXs = useMediaQuery(theme.breakpoints.only('xs'));
    const [previewUrl, setPreviewUrl] = useState('');
    const dispatch = useAppDispatch();
    const [uniqueSources, setUniqueSources] = useState([]);
    const [previousMessageId, setPreviousMessageId] = useState('');
    const [highlightData, setHighlightData] = useState([]);
    const [previousDocumentName, setPreviousDocumentName] = useState('');
    const [highlights, setHighlights] = useState([]);
    const [currentSourceIndex, setCurrentSourceIndex] = useState(currentSource.document_id);
    const [dropdownOptions, setDropdownOptions] = useState([]);
    const isBelowLg = useMediaQuery(theme.breakpoints.down('lg'));
    const [hasScrolledToHighlight, setHasScrolledToHighlight] = useState(false);
    const pdfContainerRef = useRef(null);
    const [isContainerReady, setIsContainerReady] = useState(false);
    // Ensure PDF container is ready with absolute positioning before PdfHighlighter initializes
    const containerRefCallback = React.useCallback((node) => {
        if (node) {
            // Store ref
            pdfContainerRef.current = node;
            // Force critical inline styles immediately
            node.style.cssText =
                'position: absolute !important; top: 0; left: 0; right: 0; bottom: 0; width: 100%; height: 100%; overflow: auto;';
            // Verify container meets library requirements
            let attempts = 0;
            const maxAttempts = 50;
            const checkReady = () => {
                void node.offsetHeight; // Force reflow
                const computed = window.getComputedStyle(node);
                const isAbsolute = computed.position === 'absolute';
                const hasParent = node.offsetParent !== null;
                if (isAbsolute && hasParent) {
                    setIsContainerReady(true);
                }
                else if (attempts < maxAttempts) {
                    attempts++;
                    node.style.position = 'absolute';
                    setTimeout(checkReady, 100);
                }
                else {
                    // Force after timeout
                    node.style.cssText =
                        'position: absolute !important; top: 0 !important; left: 0 !important; right: 0 !important; bottom: 0 !important; width: 100% !important; height: 100% !important;';
                    setIsContainerReady(true);
                }
            };
            setTimeout(checkReady, 50);
        }
        else {
            pdfContainerRef.current = null;
            setIsContainerReady(false);
        }
    }, []);
    const mapApiResponsesToIHighlights = (sources) => {
        console.log('Setting highlights');
        console.log('Hash ID', hash_id);
        console.log(sources);
        const highlights = [];
        sources.forEach((doc) => {
            // Match the current source by ID
            const docId = doc.doc_content_id ?? doc.chunk_id;
            const currentId = currentSource?.doc_content_id ?? currentSource?.chunk_id;
            if (docId !== currentId)
                return; // Skip if not the current source
            if (doc.location.length === 0)
                return;
            const defaultDimensions = { width: 612, height: 792 };
            const dimensions = currentSource && currentSource.width > 0 && currentSource.height > 0
                ? { width: currentSource.width, height: currentSource.height }
                : defaultDimensions;
            const rects = doc.location.map((rect) => ({
                x1: rect.xmin,
                y1: rect.ymin,
                x2: rect.xmax,
                y2: rect.ymax,
                width: dimensions.width,
                height: dimensions.height
            }));
            const boundingRect = {
                x1: Math.min(...rects.map((rect) => rect.x1)),
                y1: Math.min(...rects.map((rect) => rect.y1)),
                x2: Math.max(...rects.map((rect) => rect.x2)),
                y2: Math.max(...rects.map((rect) => rect.y2)),
                width: Math.max(...rects.map((rect) => rect.width)),
                height: Math.max(...rects.map((rect) => rect.height))
            };
            const highlight = {
                id: docId,
                content: { text: doc.content },
                position: {
                    boundingRect,
                    rects,
                    pageNumber: doc.page_number
                },
                comment: {
                    emoji: '',
                    text: ''
                }
            };
            highlights.push(highlight);
        });
        return highlights;
    };
    // Unified fetch using document_path
    const fetchPreviewUrl = useCallback(async (documentPath) => {
        try {
            const matchingSource = sources.find((source) => source.document_path === documentPath);
            const documentId = matchingSource?.document_id || currentSource.document_id;
            const docHighlights = mapApiResponsesToIHighlights(sources.filter((source) => source.document_path === documentPath));
            setHighlights(docHighlights);
            const response = await dispatch(getDocumentByPathAsync(documentPath)).unwrap();
            const reader = new FileReader();
            setCurrentSourceIndex(documentId);
            reader.onloadend = () => {
                const base64data = reader.result;
                const dataUrl = `data:application/pdf;base64,${base64data.split(',')[1]}`;
                setTimeout(() => setPreviewUrl(dataUrl), 300);
            };
            reader.readAsDataURL(response);
        }
        catch (error) {
            console.error('Error fetching document:', error);
        }
    }, [dispatch, sources, currentSource]);
    useEffect(() => {
        if (currentSource.document_path) {
            fetchPreviewUrl(currentSource.document_path);
        }
    }, [currentSource, fetchPreviewUrl]);
    useEffect(() => {
        if (scroll_id && !hasScrolledToHighlight) {
            setHighlights(highlightData);
            document.location.hash = `highlight-${hash_id}`;
            const highlight = getHighlightById(parseIdFromHash());
            if (highlight) {
                if (previousMessageId !== currentSource.doc_content_id) {
                    setTimeout(() => {
                        scrollViewerTo.current(highlight);
                    }, 3500);
                }
                else {
                    if (previousDocumentName !== currentSource.document_name) {
                        setTimeout(() => {
                            scrollViewerTo.current(highlight);
                        }, 3500);
                    }
                    else {
                        setTimeout(() => {
                            scrollViewerTo.current(highlight);
                        }, 500);
                    }
                }
                setPreviousMessageId(currentSource.doc_content_id);
            }
        }
    }, [scroll_id]);
    useEffect(() => {
        setHighlightData([]);
        setHighlightData(mapApiResponsesToIHighlights(sources));
    }, [currentSource]);
    const scrollViewerTo = useRef((highlight) => {
        // Implement scrolling logic here
    });
    useEffect(() => {
        setUniqueSources(getUniqueSources(sources));
    }, [sources]);
    const getUniqueSources = (sources) => {
        const uniqueSources = Array.from(new Map(sources.map((source) => [source.document_id, source])).values());
        return uniqueSources;
    };
    const getHighlightById = (id) => {
        return highlightData.find((highlight) => highlight.id === id);
    };
    // Use the function
    const parseIdFromHash = () => document.location.hash.slice('#highlight-'.length);
    const resetHash = () => {
        document.location.hash = '';
    };
    const scrollToHighlightFromHash = useCallback(() => {
        if (hasScrolledToHighlight)
            return; // Prevent repeated scrolling
        const highlight = getHighlightById(parseIdFromHash());
        if (highlight) {
            const target = document.getElementById(`highlight-${hash_id}`);
            if (target && target.offsetParent) {
                scrollViewerTo.current(highlight);
                setHasScrolledToHighlight(true); // Mark as scrolled
            }
        }
    }, [hasScrolledToHighlight, hash_id, getHighlightById]);
    useEffect(() => {
        window.addEventListener('hashchange', scrollToHighlightFromHash, false);
        return () => {
            window.removeEventListener('hashchange', scrollToHighlightFromHash, false);
        };
    }, [scrollToHighlightFromHash]);
    return (_jsx(Stack, { sx: {
            height: 'calc(100svh - 110px)',
            borderRadius: 2,
            padding: 2,
            backgroundColor: theme.palette.background.paper,
            boxShadow: theme.customShadows.card
        }, children: _jsxs(Stack, { sx: {
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                gap: '8px',
                height: '100%'
            }, children: [_jsxs(Box, { display: 'flex', flexDirection: 'row', justifyContent: 'space-between', width: '100%', children: [_jsx(Typography, { variant: 'h4', color: theme.palette.text.primary, marginLeft: isBelowLg ? 5 : 0, children: t('references') }), _jsxs(Box, { display: 'flex', gap: 1, flexDirection: 'row', alignItems: 'center', children: [_jsx(Button, { onClick: () => {
                                        setZoomView();
                                        onClose();
                                    }, endIcon: _jsx(ArrowOutward, {}), variant: 'contained', size: 'medium', color: 'primary', children: t('backToChat') }), !isBelowLg && (_jsx(IconButton, { onClick: () => onZoomToggle(), size: 'medium', sx: { border: '1px solid #919EAB33' }, color: 'primary', children: _jsx(SearchZoomIn, { color: theme.palette.action.active }) }))] })] }), _jsxs(Box, { gap: 0, display: 'flex', flexDirection: isBelowLg ? 'column' : 'row', justifyContent: 'center', alignItems: 'center', height: isBelowLg ? '90%' : 'calc(100svh - 170px)', width: '100%', children: [isBelowLg && (_jsx(Box, { width: '100%', mb: 1, children: _jsx(Box, { display: 'flex', flexDirection: 'row', padding: '12px 8px 12px 8px', borderRadius: '0px 16px 16px 0px', bgcolor: 'white', alignItems: 'flex-start', zIndex: 10000, gap: 1, boxShadow: theme.customShadows.z8, sx: { overflowX: 'auto' }, children: uniqueSources.map((source, index) => (_jsxs(Box, { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5, padding: '8px', sx: {
                                        cursor: 'pointer',
                                        opacity: currentSourceIndex === source.document_id ? 1 : 0.7,
                                        transition: 'opacity 0.3s, background-color 0.3s',
                                        boxShadow: theme.customShadows.z4,
                                        borderTopRightRadius: 2,
                                        borderBottomRightRadius: 2
                                    }, onClick: () => {
                                        onReferenceClick(source, sources);
                                    }, children: [_jsx(Box, { padding: '4px', bgcolor: '#F4F6F8', borderRadius: 1, sx: {
                                                filter: currentSourceIndex === source.document_id ? 'none' : 'grayscale(100%)'
                                            }, children: _jsx(Icon, { icon: 'material-icon-theme:pdf', width: 30, height: 30, color: currentSourceIndex === source.document_id ? '#e53e3e' : '#77797b' }) }), _jsx(Typography, { overflow: 'none', textOverflow: 'ellipsis', textAlign: 'center', fontSize: 10, children: source.document_name || 'file_name_pdf.pdf' })] }, index))) }) })), _jsx(Box, { p: 1, sx: {
                                width: '100%',
                                height: '100%',
                                backgroundColor: 'white',
                                boxShadow: theme.customShadows.z8,
                                borderRadius: '16px',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                position: 'relative',
                                zIndex: 999,
                                overflow: 'hidden'
                            }, children: previewUrl && highlightData ? (_jsx(PdfLoader, { url: previewUrl, beforeLoad: _jsxs(Typography, { children: [t('loadingPDF'), "..."] }), children: (pdfDocument) => (_jsx("div", { ref: containerRefCallback, className: 'pdf-container', style: {
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        width: '100%',
                                        height: '100%',
                                        overflow: 'auto'
                                    }, onContextMenu: (e) => e.preventDefault(), children: isContainerReady ? (_jsx(PdfHighlighter, { pdfScaleValue: 'page-width', pdfDocument: pdfDocument, onScrollChange: () => { }, scrollRef: (scrollTo) => {
                                            scrollViewerTo.current = scrollTo;
                                            scrollToHighlightFromHash();
                                        }, onSelectionFinished: (_, content) => {
                                            const selectedContent = content.text ?? '';
                                            return (_jsx(TranslateSelection, { selectedContent: selectedContent, dropdownOptions: dropdownOptions }));
                                        }, enableAreaSelection: (event) => event.altKey, highlightTransform: (highlight, index, setTip, hideTip, viewportToScaled, screenshot, isScrolledTo) => {
                                            return (_jsx(Highlight, { isScrolledTo: isScrolledTo, position: highlight.position, comment: { emoji: '', text: '' }, onMouseOver: () => {
                                                    const selectedContent = highlight.content.text ?? '';
                                                    setTip(highlight, () => (_jsx(TranslateSelection, { selectedContent: selectedContent, dropdownOptions: dropdownOptions })));
                                                } }, index));
                                        }, highlights: highlights })) : (_jsx(Box, { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', children: _jsxs(Typography, { children: [t('loadingPDF'), "..."] }) })) })) })) : (_jsxs(Typography, { variant: 'body1', children: [t('loadingPDF'), "..."] })) }), !isBelowLg && (_jsx(Box, { className: 'document-viewer', height: '100%', children: _jsx(Box, { maxHeight: 'calc(100svh - 190px)', display: 'flex', flexDirection: 'column', padding: '12px 0px 12px 16px', borderRadius: '0px 16px 16px 0px', alignItems: 'flex-start', 
                                // bgcolor='#F4F6F8'
                                sx: { overflowY: 'auto' }, children: uniqueSources.map((source, index) => (_jsxs(Box, { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5, padding: '12px 8px 12px 8px', sx: {
                                        cursor: 'pointer',
                                        opacity: currentSourceIndex === source.document_id ? 1 : 0.8,
                                        transition: 'opacity 0.3s, background-color 0.3s',
                                        boxShadow: theme.customShadows.z4,
                                        borderTopRightRadius: '16px',
                                        borderBottomRightRadius: '16px'
                                    }, onClick: () => {
                                        onReferenceClick(source, sources);
                                    }, children: [_jsx(Box, { padding: '4px', bgcolor: '#F4F6F8', borderRadius: 1, sx: {
                                                filter: currentSourceIndex === source.document_id ? 'none' : 'grayscale(100%)'
                                            }, children: _jsx(Icon, { icon: 'material-icon-theme:pdf', width: 30, height: 30, color: currentSourceIndex === source.document_id ? '#e53e3e' : '#77797b' }) }), _jsx(Typography, { noWrap: true, textAlign: 'center', fontSize: 10, sx: {
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                maxWidth: 60,
                                                mx: 'auto'
                                            }, children: source.document_name || 'file_name_pdf.pdf' })] }, index))) }) }))] })] }) }));
};
export default ChatReference;
